import { useCallback, useEffect, useReducer, useRef } from 'react'
import { deviceIdentityFrom } from './device-identity'
import { preloadDeviceMap } from './device-map'

export const PHASES = { entry: 'entry', checking: 'checking', confirm: 'confirm', success: 'success', expired: 'expired' }
// Accepted code lengths.
//
// The server issues four digits today and will issue more later. The two-phase
// rollout depends on this client accepting BOTH before the server changes:
// otherwise the day the length moves, nobody can approve anything, and a
// deployed approver cannot be updated on our schedule.
//
// CODE_LENGTH stays exported as the minimum, because callers use it to decide
// when a pasted string looks like a code.
export const CODE_LENGTH = 4
export const CODE_LENGTH_MIN = 4
export const CODE_LENGTH_MAX = 8
export const AUTO_SUBMIT_MS = 220
export const CHECKING_MIN_MS = 1400
export const SUCCESS_CLOSE_MS = 2400
export const ERROR_CLEAR_MS = 420

/**
 * Digits to type from a clipboard string, or null if it is not a code.
 *
 * Requires EXACTLY the length the server advertises, not the 4..8 range of
 * lengths it could advertise. Accepting the range meant a 4-digit clipboard
 * against a 6-digit server filled four wells and stopped -- never locked, so
 * never submitted, and no "nothing on the clipboard" hint either, because the
 * paste counted as accepted. An 8-digit clipboard was worse: the reducer capped
 * it at six and auto-submitted the first six digits as though they were a code.
 *
 * Non-digits are stripped first so "Your code is 504028" works, which also means
 * stray digits in surrounding text ("expires in 2 minutes") make the string the
 * wrong length. Refusing that is deliberate -- the alternative is guessing which
 * digits were the code.
 */
export const codeFromClipboard = (text, codeLength) => {
  const digits = (text || '').replace(/\D/g, '')
  return digits.length === codeLength ? digits : null
}

// The message of a refused request: auth answers {code, message} (e.g. a
// session that may not approve a device code); older routes {error}.
export const serverMessage = e => (e && e.data && (e.data.message || e.data.error)) || null

// What the keypad says when a lookup does not find a device (owner report
// 2026-10-08). A code no device is waiting on -- mistyped, expired, already
// used, or another environment's -- is an expected answer, not a fault: one
// line, no support reference. Auth answers it as 404 with this sentence (the
// same one for every kind of miss); an older auth answered core's catch-all
// "an error occured", which reads as this too.
export const NO_DEVICE = 'No device is waiting for that code. Check the code on your device; it may have expired.'
export const TOO_MANY_TRIES = 'Too many tries. Wait a moment, then enter the code again.'
// One line at any width: the reference takes the row's second line.
export const LOOKUP_FAILED = 'Something went wrong. Please try again.'
export const NO_CONNECTION = "Couldn't reach the server. Check your connection and try again."

// Core's catch-all bodies say nothing a person can use.
const GENERIC = /^(an? error occu?rr?ed|missing param|too many requests)\.?$/i

const readable = e => {
  const message = serverMessage(e)
  return typeof message === 'string' && message.trim() && !GENERIC.test(message.trim()) ? message : null
}

// { message, failure, fault } for a failed lookup (or approval). `fault` is
// a real failure -- a 5xx, or no answer at all (a network failure: api-new
// throws undefined) -- as opposed to a refusal (any 4xx: the server's own
// sentence or a plain one). Only a 5xx carries `failure`, the request whose
// reference support needs; a network failure has no request to reference.
export const lookupRefusal = e => {
  const status = e && typeof e.status === 'number' ? e.status : 0
  if (!status) return { message: NO_CONNECTION, failure: null, fault: true }
  if (status >= 500) return { message: LOOKUP_FAILED, failure: e, fault: true }
  if (status === 429) return { message: TOO_MANY_TRIES, failure: null, fault: false }
  // 404 (every kind of miss), 400/409/410 (older auths), 403 (a session that
  // may not approve): the server's sentence when it wrote one for people.
  return { message: readable(e) || NO_DEVICE, failure: null, fault: false }
}

export const initialState = {
  phase: 'entry',
  digits: '',
  // Replaced by whatever the server advertises; the default is what it issues
  // today, so a client that cannot reach the settings endpoint still works.
  codeLength: CODE_LENGTH,
  locked: false,
  erroring: false,
  shake: 0,
  device: null,
  error: null,
  // the failed request behind `error`, for its reference (error-reference.js):
  // only a real failure (5xx) has one
  failure: null,
  // `error` is a real failure (5xx or no answer), not a refusal
  fault: false,
  // bumped when a failed lookup's digits are cleared: the wells put focus
  // back on the first box so the code can be typed again
  retry: 0
}

export const reducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      // Keep the learned length: it describes the server, not this attempt.
      return { ...initialState, codeLength: state.codeLength }
    case 'digit': {
      if (state.phase !== 'entry' || state.locked || state.erroring) return state
      if (state.digits.length >= state.codeLength) return state
      const digits = state.digits + action.digit
      return { ...state, digits, locked: digits.length >= state.codeLength, error: null, failure: null, fault: false }
    }
    // The server told us how many digits it issues. Adopting it here rather than
    // hard-coding a length is what lets the length change without a client
    // release: a deployed approver cannot be updated on our schedule, and one
    // that guessed wrong can no longer approve anything.
    case 'codeLength': {
      const codeLength = Math.min(Math.max(action.codeLength, CODE_LENGTH_MIN), CODE_LENGTH_MAX)
      return { ...state, codeLength }
    }
    case 'backspace': {
      if (state.phase !== 'entry' || state.locked || state.erroring) return state
      if (state.digits.length === 0) return { ...state, shake: state.shake + 1 }
      return { ...state, digits: state.digits.slice(0, -1) }
    }
    case 'checking':
      return { ...state, phase: 'checking', error: null, failure: null, fault: false }
    // A device lands only on the screens that asked for one (the lookup, or
    // the confirm screen's expiry re-check): a late answer never moves a
    // finished or fresh attempt.
    case 'found':
      if (state.phase !== 'checking' && state.phase !== 'confirm') return state
      return { ...state, phase: 'confirm', device: action.device, error: null, failure: null, fault: false }
    // Failed lookup: keep the wrong digits so the wells shake with them still shown,
    // then a clearError (scheduled by the hook) wipes them after the shake. Only
    // while that lookup is the one being checked: a late answer is dropped.
    // A refusal shakes; a real failure (`fault`: a 5xx, or no answer) is not a
    // wrong code.
    case 'lookupFailed':
      if (state.phase !== 'checking') return state
      return { ...state, phase: 'entry', locked: false, erroring: true, shake: action.fault ? state.shake : state.shake + 1, error: action.error || null, failure: (action.error && action.failure) || null, fault: !!(action.error && action.fault) }
    // The message stays on screen after the shake, until the next digit; the
    // first box takes focus again.
    case 'clearError':
      return { ...state, digits: '', erroring: false, retry: state.retry + 1 }
    case 'success':
      return { ...state, phase: 'success' }
    // Only the confirm screen can lapse into expired — a race with approve
    // (success already dispatched) must not be yanked back.
    case 'expired':
      if (state.phase !== 'confirm') return state
      return { ...state, phase: 'expired', error: null, failure: null, fault: false }
    case 'approveFailed':
      return { ...state, phase: 'confirm', error: action.error || null, failure: (action.error && action.failure) || null, fault: !!(action.error && action.fault) }
    default:
      return state
  }
}

const minDelay = (startedMs, ms) => {
  const remaining = ms - (Date.now() - startedMs)
  return remaining > 0 ? new Promise(r => setTimeout(r, remaining)) : Promise.resolve()
}

export const useDeviceCode = ({ api, onClose }) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const epochRef = useRef(0)

  // api is NOT stable. useApiForm returns useMemo(() => ({...state, ...actions}),
  // [state, actions]), so every request-state transition — LOADING, then SUCCESS
  // or ERROR — hands back a new object. Listing it as an effect dependency makes
  // the effect re-run in response to its own request, which is an infinite loop,
  // and one that success does not break out of.
  //
  // It cost us both bugs below: the settings fetch re-fired forever (thousands of
  // requests at the auth service, and enough of a flood that Safari began failing
  // them as access-control errors), and the auto-submit effect was torn down and
  // rebuilt faster than its own 220ms timer, so a fully entered code sat there
  // and never advanced.
  //
  // Read it through a ref: effects always see the current api without ever
  // re-running because its identity changed.
  const apiRef = useRef(api)
  apiRef.current = api

  // Learn the server's code length once. Best effort: a failure leaves the
  // default, which is what the server issues today, so this can never make the
  // modal unusable — it only lets the length move later without a client
  // release.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const settings = await apiRef.current.get('../auth-device-settings')
        const length = settings && Number(settings.codeLength)
        if (!cancelled && length) dispatch({ type: 'codeLength', codeLength: length })
      } catch (e) { /* keep the default */ }
    }
    load()
    return () => { cancelled = true }
    // Once per mount. The length describes the server, not this attempt.
  }, [])

  const pressDigit = useCallback(d => dispatch({ type: 'digit', digit: d }), [])
  const backspace = useCallback(() => dispatch({ type: 'backspace' }), [])
  const reset = useCallback(() => { epochRef.current++; dispatch({ type: 'reset' }) }, [])

  // Reject invalidates the pending code server-side (best-effort) so the requesting
  // device's poll returns 404 and it fetches a fresh code, then returns to entry.
  //
  // The reason matters. Only an explicit Deny press is evidence about the device
  // that ASKED for the code, and that is the party abuse detection watches.
  // Dismissing the screen also denies the code, but people close modals for all
  // sorts of reasons, so counting those would accuse legitimate requesters.
  const reject = useCallback(async (reason = 'explicit') => {
    const code = state.digits
    epochRef.current++
    try { await api.delete(`${code}?reason=${encodeURIComponent(reason)}`) } catch (e) { /* best-effort invalidation */ }
    dispatch({ type: 'reset' })
  }, [state.digits, api])

  // Auto-submit + lookup once the entered code reaches the server's length.
  useEffect(() => {
    if (state.phase !== 'entry' || !state.locked) return
    const code = state.digits
    const epoch = ++epochRef.current
    const timer = setTimeout(async () => {
      dispatch({ type: 'checking' })
      const started = Date.now()
      try {
        const result = await apiRef.current.get(`${code}/find`)
        const identity = deviceIdentityFrom(result)
        const device = { ...identity, code, requestedAt: new Date(), expiresAt: result && result.expiresAt }
        // Fire the map fetch now (this lookup has just bound the code to
        // this session, which auth's map asks for), in parallel with the
        // artificial CHECKING_MIN_MS floor below, so the confirm screen's
        // DeviceMap has a head start instead of starting cold.
        if (device.location) preloadDeviceMap(code)
        await minDelay(started, CHECKING_MIN_MS)
        if (epoch !== epochRef.current) return
        dispatch({ type: 'found', device })
      } catch (e) {
        await minDelay(started, CHECKING_MIN_MS)
        if (epoch !== epochRef.current) return
        const { message, failure, fault } = lookupRefusal(e)
        dispatch({ type: 'lookupFailed', error: message, failure, fault })
      }
    }, AUTO_SUBMIT_MS)
    return () => clearTimeout(timer)
  }, [state.phase, state.locked, state.digits])

  const approve = useCallback(async () => {
    const code = state.digits
    try {
      await api.put(code)
      dispatch({ type: 'success' })
    } catch (e) {
      const { message, failure, fault } = lookupRefusal(e)
      dispatch({ type: 'approveFailed', error: message, failure, fault })
    }
  }, [state.digits, api])

  // Confirm-phase expiry watch: when the code's lifetime lapses, verify against
  // the server before flipping (client clocks skew) — still valid there means
  // our clock ran ahead, so re-arm from the fresh expiresAt; expired or gone
  // means the approve decision is moot and the user gets the expired view.
  useEffect(() => {
    if (state.phase !== 'confirm') return
    const expiresAt = state.device && state.device.expiresAt
    if (!expiresAt) return
    const ms = new Date(expiresAt).getTime() - Date.now()
    const epoch = epochRef.current
    const timer = setTimeout(async () => {
      try {
        const result = await apiRef.current.get(`${state.digits}/find`)
        if (epoch !== epochRef.current) return
        const fresh = result && result.expiresAt
        if (fresh && new Date(fresh).getTime() > Date.now()) {
          dispatch({ type: 'found', device: { ...state.device, expiresAt: fresh } })
          return
        }
        dispatch({ type: 'expired' })
      } catch (e) {
        if (epoch !== epochRef.current) return
        dispatch({ type: 'expired' })
      }
    }, Math.max(0, ms))
    return () => clearTimeout(timer)
  }, [state.phase, state.device, state.digits])

  // After a failed lookup, let the wells shake with the wrong digits, then clear them.
  useEffect(() => {
    if (!state.erroring) return
    const timer = setTimeout(() => dispatch({ type: 'clearError' }), ERROR_CLEAR_MS)
    return () => clearTimeout(timer)
  }, [state.erroring])

  // Success auto-close.
  useEffect(() => {
    if (state.phase !== 'success') return
    const timer = setTimeout(() => onClose && onClose(), SUCCESS_CLOSE_MS)
    return () => clearTimeout(timer)
  }, [state.phase, onClose])

  return { state, pressDigit, backspace, reject, approve, reset }
}
